import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Flame, Thermometer, CalendarClock, AlertTriangle, MessageCircle, Copy, Sun } from "lucide-react";
import { toast } from "sonner";

const Tile = ({ icon: Icon, label, value, tone, testid }) => (
  <div data-testid={testid} className={`rounded-xl p-3 border ${tone}`}>
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4" />
      <span className="text-[11px] font-medium opacity-80">{label}</span>
    </div>
    <p className="font-display text-2xl font-extrabold mt-1 leading-none">{value}</p>
  </div>
);

export default function BriefingCard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/briefing/today").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === false) return null;
  if (!data) return <Skeleton className="h-64 rounded-2xl mb-6" />;

  const s = data.stats || {};
  const overdue = s.overdue > 0;
  const waLink = `https://wa.me/${data.whatsapp}?text=${encodeURIComponent(data.message)}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.message); toast.success("Briefing copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <Card data-testid="briefing-card" className="p-5 mb-6 border-gray-100 shadow-sm bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Sun className="h-5 w-5 text-amber-400" />Today's Briefing</h3>
          <p className="text-xs text-slate-300">{data.date} · your hot leads & follow-ups at a glance</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={copy} variant="outline" size="sm" className="gap-1.5 bg-transparent text-white border-slate-600 hover:bg-slate-700 hover:text-white" data-testid="briefing-copy"><Copy className="h-4 w-4" />Copy</Button>
          <a href={waLink} target="_blank" rel="noreferrer">
            <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700" data-testid="briefing-send-whatsapp"><MessageCircle className="h-4 w-4" />Send to me on WhatsApp</Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        <Tile icon={Flame} label="Hot" value={s.hot || 0} tone="bg-orange-500/15 border-orange-500/30 text-orange-200" testid="briefing-stat-hot" />
        <Tile icon={Thermometer} label="Warm" value={s.warm || 0} tone="bg-yellow-500/15 border-yellow-500/30 text-yellow-200" testid="briefing-stat-warm" />
        <Tile icon={CalendarClock} label="Open follow-ups" value={s.open_followups || 0} tone="bg-sky-500/15 border-sky-500/30 text-sky-200" testid="briefing-stat-open" />
        <Tile icon={AlertTriangle} label="Overdue" value={s.overdue || 0} tone={overdue ? "bg-red-500/25 border-red-500/50 text-red-200" : "bg-slate-500/15 border-slate-500/30 text-slate-200"} testid="briefing-stat-overdue" />
      </div>

      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">WhatsApp-ready message</p>
        <pre data-testid="briefing-message" className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-slate-100 bg-slate-950/40 rounded-xl p-3 max-h-72 overflow-y-auto">{data.message}</pre>
      </div>
    </Card>
  );
}
